import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bearing } from '@turf/bearing';
import Polyline from './layers/Polyline';
import Marker from './markers/Marker';
import HereMap from './HereMap';
import { itemSVG, toLngLat } from '../../lib/map_helpers';
import Area from './layers/Area';

const generateDarkColor = (index) => {
  const baseHue = 180;
  const hueStep = 40;
  return `hsl(${(baseHue + index * hueStep) % 360}, 50%, 30%)`;
};

class RoutingTestsMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      waypoints: props.routingTest.waypoints,
      route: props.routingTest.route
    };
  }

  renderWaypointMarkers = () => {
    const { waypoints } = this.state;
    if (!waypoints) return null;

    return waypoints.map((waypoint, index) => {
      if (!waypoint?.length) return null;

      const color = generateDarkColor(index);
      const icon = {
        url: itemSVG(color, index + 1),
        size: [40, 40],
        anchor: 'center'
      };

      const key = `waypoint-marker-${waypoint[0]}-${waypoint[1]}`;

      return (
          <Marker
              key={key}
              position={waypoint}
              icon={icon}
              zIndexOffset={800}
              type="waypoint"
          />
      );
    });
  };

  renderRoute = () => {
    const { route } = this.state;
    if (!route?.length) return null;

    return <Polyline positions={route} color="#222222" weight={5} opacity={0.3} withDirectionArrows />;
  };

  renderRestrictedAreas = () => {
    const { restrictedAreas } = this.props;
    if (!restrictedAreas) return null;

    return restrictedAreas.map((area) => {
      if (!area?.id || !area.bounding_box) return null;

      return (
          <Area
              key={area.id}
              area={area.area_type === 'bbox' ? area.bounding_box : area.area}
              color="#ff2b79"
              opacity={0.8}
              fillColor="#ff2b79"
          />
      );
    });
  };

  renderApproachArrows = () => {
    const { route, waypoints } = this.state;
    if (!route?.length || !waypoints || waypoints.length <= 2) return null;

    const epsilon = 0.00001;
    return waypoints.slice(1, -1).map((viaWaypoint, i) => {
      if (!viaWaypoint?.length) return null;

      const currentViaWaypointIndexInRoute = route.findIndex(
          (p) =>
              p?.length >= 2 &&
              Math.abs(p[0] - viaWaypoint[0]) < epsilon &&
              Math.abs(p[1] - viaWaypoint[1]) < epsilon
      );

      const prevMajorWaypoint = waypoints[i];
      if (!prevMajorWaypoint?.length) return null;

      const prevMajorWaypointIndexInRoute = route.findIndex(
          (p) =>
              p?.length >= 2 &&
              Math.abs(p[0] - prevMajorWaypoint[0]) < epsilon &&
              Math.abs(p[1] - prevMajorWaypoint[1]) < epsilon
      );

      if (
          currentViaWaypointIndexInRoute > 0 &&
          prevMajorWaypointIndexInRoute !== -1 &&
          currentViaWaypointIndexInRoute > prevMajorWaypointIndexInRoute + 2
      ) {
        const [p1, p2] = [
          route[currentViaWaypointIndexInRoute - 1],
          route[currentViaWaypointIndexInRoute]
        ];
        if (!p1?.length || !p2?.length) return null;

        const arrowSvg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20">
            <polygon points="10,0 16,14 4,14" style="fill:red;" />
          </svg>
        `;

        const arrowIcon = {
          html: arrowSvg,
          className: 'approach-arrow-icon',
          size: [20, 20],
          anchor: 'center'
        };

        return (
            <Marker
                key={`approach-arrow-${i}`}
                position={p1}
                icon={arrowIcon}
                rotation={bearing(toLngLat(p1), toLngLat(p2))}
                zIndexOffset={790}
            />
        );
      }
      return null;
    });
  };

  render() {
    return (
        <HereMap boundingBox={this.props.boundingBox} autoZoom noTraffic>
          {this.renderRestrictedAreas()}
          {this.renderRoute()}
          {this.renderApproachArrows()}
          {this.renderWaypointMarkers()}
        </HereMap>
    );
  }
}

RoutingTestsMap.propTypes = {
  boundingBox: PropTypes.object.isRequired,
  routingTest: PropTypes.object.isRequired,
  restrictedAreas: PropTypes.array
};

export default RoutingTestsMap;
